/* eslint react-hooks/exhaustive-deps: "warn" */
import { type CSSProperties, Fragment, useRef, type JSX } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import styles from './TechnologySecurity.module.scss';

type Translate = (key: string) => string;

type Segment = {
  text: string;
  bold?: boolean;
};

type Item = {
  title: string;
  paragraph: Segment[];
  icon: 'data' | 'shield' | 'cert' | 'globe' | 'clock' | 'building';
};

const getItems = (t: Translate): Item[] => [
  {
    title: t('What the data actually is'),
    icon: 'data',
    paragraph: [
      {
        text: t(
          'Public-record company information published by government registers. Officer and beneficial-owner details are the ones the register itself publishes. ',
        ),
      },
      { text: t('No consumer personal data'), bold: true },
      { text: t(' is collected or resold.') },
    ],
  },
  {
    title: t('Data protection'),
    icon: 'shield',
    paragraph: [
      {
        text: t(
          'UK GDPR and EU GDPR. A DPA and a current sub-processor list are available on request and form part of the security pack.',
        ),
      },
    ],
  },
  {
    title: t('Certification'),
    icon: 'cert',
    paragraph: [
      { text: t('ISO 27001 certified in the United Kingdom.'), bold: true },
      { text: t(' Certificate number, issuing body, scope and expiry are supplied with the security pack.') },
    ],
  },
  {
    title: t('Hosting and residency'),
    icon: 'globe',
    paragraph: [
      { text: t('Data is hosted in the European Union.'), bold: true },
      {
        text: t(
          ' Sub-processor locations are listed in the pack. Other residency arrangements are handled contractually.',
        ),
      },
    ],
  },
  {
    title: t('Retention and exit'),
    icon: 'clock',
    paragraph: [
      {
        text: t(
          'Retention, deletion and post-termination handling of retrieved records are set in the MSA rather than left to policy. Data already delivered under a bulk licence stays under the terms it arrived on.',
        ),
      },
    ],
  },
  {
    title: t('Contracting entities'),
    icon: 'building',
    paragraph: [
      { text: 'Global Data Intelligence Limited', bold: true },
      { text: t(', registered in England and Wales, company number ') },
      { text: '09410808', bold: true },
      { text: t(', VAT GB260423730 — the contracting entity for UK and EU customers. ') },
      { text: 'Global Database North America Inc.', bold: true },
      { text: t(' contracts customers in the Americas.') },
    ],
  },
];

const ICONS: Record<Item['icon'], JSX.Element> = {
  data: (
    <>
      <path d="M4 6h16M4 12h16M4 18h10" />
    </>
  ),
  shield: (
    <path
      d="M12 3.5 19.5 6.5V11.5C19.5 16 16.3 19.6 12 20.5C7.7 19.6 4.5 16 4.5 11.5V6.5L12 3.5Z"
      strokeLinejoin="round"
    />
  ),
  cert: (
    <>
      <circle cx="12" cy="9.5" r="5.5" />
      <path d="M9 14.3 8 21l4-2.2L16 21l-1-6.7" />
    </>
  ),
  globe: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M3.5 12h17M12 3.5c2.6 2.3 4 5.3 4 8.5s-1.4 6.2-4 8.5c-2.6-2.3-4-5.3-4-8.5s1.4-6.2 4-8.5z" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2.5" />
    </>
  ),
  building: (
    <>
      <path d="M5 21V4.5h9V21M14 21V9h5v12M8.5 8h1M8.5 11.5h1M8.5 15h1" />
    </>
  ),
};

export const TechnologySecurity = () => {
  const { t } = useInt();
  const items = getItems(t);
  const gridRef = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(gridRef, { threshold: 0.1 });

  return (
    <section className={styles.root}>
      <div className={styles.glow} />

      <div className={styles.shell}>
        <span className={styles.kick}>
          <i className={styles.kickLine} />
          {t('security and procurement')}
        </span>
        <TitleBlock align="left" theme="dark" title={t('Everything your security review will ask for.')} />

        <div className={cn(styles.grid, revealed && styles.gridOn)} ref={gridRef}>
          {items.map((item, i) => (
            <div className={styles.item} style={{ '--d': `${i * 60}ms` } as CSSProperties} key={item.title}>
              <span className={styles.icon}>
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  {ICONS[item.icon]}
                </svg>
              </span>
              <h3>{item.title}</h3>
              <p>
                {item.paragraph.map((segment, j) => (
                  <Fragment key={j}>{segment.bold ? <b>{segment.text}</b> : segment.text}</Fragment>
                ))}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
